import React, { useCallback, useMemo, useRef } from "react";
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    DragEndEvent,
    Modifier,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useActionsContext } from "./ActionsContext";
import { ActionRow } from "./ActionRow";
import styles from "./ActionsCard.module.scss";

function clamp(value: number, min: number, max: number): number {
    const lower = Math.min(min, max);
    const upper = Math.max(min, max);
    return Math.min(Math.max(value, lower), upper);
}

export const ActionsTable: React.FC = () => {
    const { rows, reorderRows, isRuleAuthoringAvailable } = useActionsContext();
    const tableWrapperRef = useRef<HTMLDivElement | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 4 },
        }),
    );

    const allRowIds = useMemo(() => rows.map((r) => r.id), [rows]);

    const restrictToTableWrapper = useCallback<Modifier>(
        ({ transform, activeNodeRect }) => {
            const wrapper = tableWrapperRef.current;
            if (!wrapper || !activeNodeRect) {
                return {
                    ...transform,
                    x: 0,
                };
            }

            const wrapperRect = wrapper.getBoundingClientRect();
            const minY = wrapperRect.top - activeNodeRect.top;
            const maxY = wrapperRect.bottom - activeNodeRect.bottom;

            return {
                ...transform,
                x: 0,
                y: clamp(transform.y, minY, maxY),
            };
        },
        [],
    );

    const modifiers = useMemo(
        () => [restrictToTableWrapper],
        [restrictToTableWrapper],
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) {
                return;
            }
            const fromIndex = allRowIds.indexOf(String(active.id));
            const toIndex = allRowIds.indexOf(String(over.id));
            if (fromIndex === -1 || toIndex === -1) {
                return;
            }
            reorderRows(fromIndex, toIndex);
        },
        [allRowIds, reorderRows],
    );

    return (
        <div ref={tableWrapperRef} className={styles.tableWrapper}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        {isRuleAuthoringAvailable && <th>Condition</th>}
                        <th>Action</th>
                        <th>Entity</th>
                        <th>Transactions</th>
                        {isRuleAuthoringAvailable && <th>Create rule</th>}
                        <th
                            className={styles.rowActionsHeader}
                            aria-label="Row actions"
                        />
                    </tr>
                </thead>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={modifiers}
                    autoScroll={{ enabled: false }}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={allRowIds}
                        strategy={verticalListSortingStrategy}
                    >
                        <tbody>
                            {rows.map((row) => (
                                <ActionRow key={row.id} row={row} />
                            ))}
                        </tbody>
                    </SortableContext>
                </DndContext>
            </table>
        </div>
    );
};
